import React, { useEffect, useState } from "react";
import { Table, Spinner, Alert, Input, Button, Form, FormGroup, Label } from "reactstrap";
import axiosInstance from "../../Request";
import { URL } from "../../api";

const LoanReporting = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState("");

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [searchValue, setSearchValue] = useState("");
    const [status, setStatus] = useState("");
    const [filterType, setFilterType] = useState("loanCode");

    // filterType maps 1:1 to the backend /reporting/loan_summary param name.
    const FILTER_LABELS = {
        loanCode: "Loan Code (LI…)",
        loanId: "Loan ID (numeric)",
        customerId: "Customer ID",
        leadId: "Lead ID",
        applicationId: "Application ID",
    };

    const [fintechList, setFintechList] = useState([]);
    const [selectedFintechId, setSelectedFintechId] = useState("");

    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 30;
    const [hasNextPage, setHasNextPage] = useState(false);
    const [activeFilters, setActiveFilters] = useState({});

    // =================== Fetch Fintech/LSP List ===================
    useEffect(() => {
        const fetchFintechs = async () => {
            try {
                const res = await axiosInstance.get(URL.LSP_LIST);
                setFintechList((res.data || []).filter((f) => f.isActive));
            } catch (err) {
                console.error("Failed to fetch fintech list:", err);
            }
        };
        fetchFintechs();
    }, []);

    useEffect(() => {
        fetchLoanSummary(1, {});
    }, []);

    const fetchLoanSummary = async (page = 1, filters = {}) => {
        setLoading(true);
        setError("");
        try {
            const params = { ...filters, page: page - 1, size: rowsPerPage };
            const res = await axiosInstance.get(URL.REPORTING_LOAN_SUMMARY, { params });
            const resultData = Array.isArray(res.data) ? res.data : [];
            setData(resultData);
            setCurrentPage(page);
            setHasNextPage(resultData.length === rowsPerPage);
        } catch (err) {
            setError("Failed to fetch loan summary data.");
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        if (e) e.preventDefault();
        const filters = {};
        if (fromDate) filters.fromDate = fromDate;
        if (toDate) filters.toDate = toDate;
        if (status) filters.status = status;
        if (selectedFintechId) filters.fintechId = selectedFintechId;
        if (searchValue && searchValue.trim()) filters[filterType] = searchValue.trim();

        setActiveFilters(filters);
        fetchLoanSummary(1, filters);
    };

    const handleExcelExport = async () => {
        if (data.length === 0) return alert("No data available to export!");
        setExporting(true);
        try {
            const params = { ...activeFilters, export: true };
            const response = await axiosInstance.get(URL.REPORTING_LOAN_SUMMARY, {
                params,
                responseType: "blob",
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `Loan_Report_${new Date().toLocaleDateString()}.csv`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Export failed:", err);
            alert("Export failed. Please check your session.");
        } finally {
            setExporting(false);
        }
    };

    const font10 = { fontSize: "10px" };
    const inputSmStyle = { ...font10, height: "30px", padding: "2px 6px" };
    const buttonSmStyle = { ...font10, padding: "4px 10px", height: "30px", lineHeight: "1" };

    return (
        <div style={font10}>
            <Form onSubmit={handleSearch} className="mb-3">
                <div className="d-flex align-items-end flex-wrap" style={{ gap: "10px" }}>

                    <FormGroup className="mb-0">
                        <Label className="mb-1 fw-bold">Filter Type</Label>
                        <Input
                            type="select"
                            bsSize="sm"
                            value={filterType}
                            onChange={(e) => { setFilterType(e.target.value); setSearchValue(""); }}
                            style={inputSmStyle}
                        >
                            {Object.entries(FILTER_LABELS).map(([val, label]) => (
                                <option key={val} value={val}>{label}</option>
                            ))}
                        </Input>
                    </FormGroup>

                    <FormGroup className="mb-0">
                        <Label className="mb-1 fw-bold">
                            {FILTER_LABELS[filterType]}
                        </Label>
                        <Input
                            bsSize="sm"
                            type="text"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            placeholder={FILTER_LABELS[filterType]}
                            style={inputSmStyle}
                        />
                    </FormGroup>

                    <FormGroup className="mb-0">
                        <Label className="mb-1 fw-bold">Status</Label>
                        <Input
                            type="select"
                            bsSize="sm"
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            style={inputSmStyle}
                        >
                            <option value="">All</option>
                            <option value="0">Pending</option>
                            <option value="1">Approved</option>
                            <option value="2">Disbursed</option>
                            <option value="3">Closed</option>
                        </Input>
                    </FormGroup>

                    <FormGroup className="mb-0">
                        <Label className="mb-1 fw-bold">From Date</Label>
                        <Input
                            type="date"
                            bsSize="sm"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            style={inputSmStyle}
                        />
                    </FormGroup>

                    <FormGroup className="mb-0">
                        <Label className="mb-1 fw-bold">To Date</Label>
                        <Input
                            type="date"
                            bsSize="sm"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            style={inputSmStyle}
                        />
                    </FormGroup>

                    <FormGroup className="mb-0">
                        <Label className="mb-1 fw-bold">Fintech</Label>
                        <Input
                            type="select"
                            bsSize="sm"
                            value={selectedFintechId}
                            onChange={(e) => setSelectedFintechId(e.target.value)}
                            style={{ ...inputSmStyle, width: 160 }}
                        >
                            <option value="">All Fintechs</option>
                            {fintechList.map((f) => (
                                <option key={f.id} value={f.id}>
                                    {f.brandName || f.name}
                                </option>
                            ))}
                        </Input>
                    </FormGroup>

                    <div className="d-flex" style={{ gap: "6px", paddingBottom: "1px" }}>
                        <Button color="primary" type="submit" style={buttonSmStyle}>
                            Search
                        </Button>
                        <Button
                            color="success"
                            onClick={handleExcelExport}
                            style={buttonSmStyle}
                            disabled={exporting}
                        >
                            {exporting ? (
                                <>
                                    <Spinner size="sm" color="light" className="me-1" />
                                    Wait...
                                </>
                            ) : (
                                "Excel"
                            )}
                        </Button>
                    </div>
                </div>
            </Form>

            {loading ? (
                <div className="text-center p-4">
                    <Spinner size="sm" /> Loading Loan Summary...
                </div>
            ) : error ? (
                <Alert color="danger" style={font10}>{error}</Alert>
            ) : (
                <div style={{ overflowX: "auto" }}>
                    <Table bordered striped hover responsive size="sm" className="mt-3 mb-0 text-nowrap">
                        {/* OLD THEAD - commented out
                        <thead className="thead-light">
                            <tr>
                                <th>#</th>
                                <th>Loan ID</th>
                                <th>Cust ID</th>
                                <th>Borrower</th>
                                <th>Fintech Name</th>
                                <th>Approved Date</th>
                                <th>Disbursed Date</th>
                                <th>Loan Amt</th>
                                <th>Disbursed Amt</th>
                                <th>UTR</th>
                                <th>Status</th>
                                <th>Outstanding</th>
                                <th>Principal</th>
                                <th>Interest</th>
                                <th>Loan Type</th>
                                <th>ROI Type</th>
                                <th>ROI (%)</th>
                            </tr>
                        </thead>
                        */}
                        <thead className="thead-light">
                            <tr>
                                <th>#</th>
                                <th>Loan ID</th>
                                <th>Cust ID</th>
                                <th>Borrower</th>
                                <th>Fintech Name</th>
                                <th>Approved Date</th>
                                <th>Disbursed Date</th>
                                <th>Loan Amt</th>
                                <th>Disbursed Amt</th>
                                <th>UTR</th>
                                <th>Status</th>
                                <th>Outstanding</th>
                                <th>Principal</th>
                                <th>Interest</th>
                                <th>Loan Type</th>
                                <th>ROI Type</th>
                                <th>ROI (%)</th>
                                <th>IGST</th>
                                <th>CGST</th>
                                <th>SGST</th>
                                <th>Pincode</th>
                                <th>State</th>
                            </tr>
                        </thead>
                        {/* OLD TBODY - commented out
                        <tbody>
                            {data.length > 0 ? (
                                data.map((row, idx) => (
                                    <tr key={idx}>
                                        <td>{(currentPage - 1) * rowsPerPage + idx + 1}</td>
                                        <td>{row.loan_id}</td>
                                        <td>{row.customer_id}</td>
                                        <td>{row.borrower_name}</td>
                                        <td>{row.fintech_name || "N/A"}</td>
                                        <td>{row.approved_date || "-"}</td>
                                        <td>{row.disbursed_date || "-"}</td>
                                        <td>₹{Number(row.loan_amount || 0).toLocaleString()}</td>
                                        <td>₹{Number(row.disbursed_amount || 0).toLocaleString()}</td>
                                        <td>{row.utr_number || "-"}</td>
                                        <td className="fw-bold">{row.status || "N/A"}</td>
                                        <td>₹{Number(row.total_outstanding_amount || 0).toLocaleString()}</td>
                                        <td>₹{Number(row.total_outstanding_principal || 0).toLocaleString()}</td>
                                        <td>₹{Number(row.total_outstanding_interest || 0).toLocaleString()}</td>
                                        <td>{row.loan_type || "-"}</td>
                                        <td>{row.roi_type || "-"}</td>
                                        <td>{row.roi ? `${row.roi}%` : "-"}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="17" className="text-center">No data found</td>
                                </tr>
                            )}
                        </tbody>
                        */}
                        <tbody>
                            {data.length > 0 ? (
                                data.map((row, idx) => (
                                    <tr key={idx}>
                                        <td>{(currentPage - 1) * rowsPerPage + idx + 1}</td>
                                        <td>{row.loan_id}</td>
                                        <td>{row.customer_id}</td>
                                        <td>{row.borrower_name}</td>
                                        <td>{row.fintech_name || "N/A"}</td>
                                        <td>{row.approved_date || "-"}</td>
                                        <td>{row.disbursed_date || "-"}</td>
                                        <td>₹{Number(row.loan_amount || 0).toLocaleString()}</td>
                                        <td>₹{Number(row.disbursed_amount || 0).toLocaleString()}</td>
                                        <td>{row.utr_number || "-"}</td>
                                        <td className="fw-bold">{row.status || "N/A"}</td>
                                        <td>₹{Number(row.total_outstanding_amount || 0).toLocaleString()}</td>
                                        <td>₹{Number(row.total_outstanding_principal || 0).toLocaleString()}</td>
                                        <td>₹{Number(row.total_outstanding_interest || 0).toLocaleString()}</td>
                                        <td>{row.loan_type || "-"}</td>
                                        <td>{row.roi_type || "-"}</td>
                                        <td>{row.roi ? `${row.roi}%` : "-"}</td>
                                        <td>₹{Number(row.igst || 0).toLocaleString()}</td>
                                        <td>₹{Number(row.cgst || 0).toLocaleString()}</td>
                                        <td>₹{Number(row.sgst || 0).toLocaleString()}</td>
                                        <td>{row.pincode || "-"}</td>
                                        <td>{row.state_name || "-"}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="22" className="text-center">No data found</td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            )}

            <div className="d-flex justify-content-center mt-3">
                <div className="d-flex align-items-center" style={{ gap: "5px" }}>
                    <Button
                        color="outline-primary"
                        size="sm"
                        style={buttonSmStyle}
                        onClick={() => fetchLoanSummary(currentPage - 1, activeFilters)}
                        disabled={currentPage === 1}
                    >
                        Prev
                    </Button>
                    {Array.from({ length: hasNextPage ? currentPage + 1 : currentPage }, (_, i) => {
                        const pageNum = i + 1;
                        if (pageNum >= currentPage - 1 && pageNum <= currentPage + 1) {
                            return (
                                <Button
                                    key={pageNum}
                                    size="sm"
                                    style={buttonSmStyle}
                                    color={currentPage === pageNum ? "primary" : "outline-primary"}
                                    onClick={() => fetchLoanSummary(pageNum, activeFilters)}
                                >
                                    {pageNum}
                                </Button>
                            );
                        }
                        return null;
                    })}
                    <Button
                        color="outline-primary"
                        size="sm"
                        style={buttonSmStyle}
                        onClick={() => fetchLoanSummary(currentPage + 1, activeFilters)}
                        disabled={!hasNextPage}
                    >
                        Next
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default LoanReporting;
